import { Currency } from '@/constants';
import { IBookBalanceDataAPI } from '@/types/bookBalance';

export class BookBalanceItem {
    public absBalanceAgentPc: number;
    public absBalanceBookmaker: number;
    public absBalanceBookmakerPc: number;
    public absBalancePc: number;
    public accountCurrencyId: number | null;
    public balanceAc: number;
    public balanceAgentAc: number;
    public balanceAgentEuro: number;
    public balanceAgentPc: number;
    public balanceBookmakerAc: number;
    public balanceBookmakerEuro: number;
    public balanceBookmakerPc: number;
    public balancePc: number;
    public betCount: number;
    public bookFeesAc: number;
    public bookFeesEuro: number;
    public bookFeesPc: number;
    public bookmakerId: number | null;
    public bookRebateAc: number;
    public bookRebateEuro: number;
    public bookRebatePc: number;
    public companyComAc: number;
    public companyComEuro: number;
    public companyComPc: number;
    public currencyId: number | null;
    public playerComAc: number;
    public playerComEuro: number;
    public playerComPc: number;
    public ptBalancePc: number;
    public settledBetCount: number;
    public settledStakeBookmakerAcSum: number;
    public settledStakeBookmakerSumPc: number;
    public settledStakePcSum: number;
    public settledStakePositionPcSum: number;

    constructor(data: IBookBalanceDataAPI) {
        this.currencyId = data.currency_id ? Number(data.currency_id) : null;
        this.accountCurrencyId = data.account_currency_id;
        this.bookmakerId = data.site_id;
        this.settledBetCount = data.settled_bet_count;
        this.betCount = data.bet_count;
        this.settledStakePcSum = data.settled_stake_pc_sum;
        this.settledStakeBookmakerAcSum = data.settled_stake_bookmaker_ac_sum;
        this.settledStakeBookmakerSumPc = data.settled_stake_bookmaker_pc_sum;
        this.settledStakePositionPcSum = data.settled_stake_position_pc_sum;
        this.balancePc = data.balance_pc;
        this.balanceAc = data.balance_ac;
        this.absBalancePc = data.abs_balance_pc;
        this.absBalanceAgentPc = data.abs_balance_agent_pc;
        this.balanceBookmakerPc = data.balance_bookmaker_pc;
        this.balanceBookmakerEuro = data.balance_bookmaker_euro;
        this.balanceBookmakerAc = data.balance_bookmaker_ac;
        this.balanceAgentPc = data.balance_agent_pc;
        this.balanceAgentEuro = data.balance_agent_euro;
        this.balanceAgentAc = data.balance_agent_ac;
        this.absBalanceBookmaker = data.abs_balance_bookmaker_ac;
        this.absBalanceBookmakerPc = data.abs_balance_bookmaker_pc;
        this.ptBalancePc = data.pt_balance_pc;
        this.companyComPc = data.company_com_pc;
        this.companyComEuro = data.company_com_euro;
        this.companyComAc = data.company_com_ac;
        this.playerComPc = data.player_com_pc;
        this.playerComAc = data.player_com_ac;
        this.playerComEuro = data.player_com_euro;
        this.bookFeesEuro = data.book_fees_euro;
        this.bookFeesAc = data.book_fees_ac;
        this.bookFeesPc = data.book_fees_pc;
        this.bookRebateEuro = data.book_rebate_euro;
        this.bookRebateAc = data.book_rebate_ac;
        this.bookRebatePc = data.book_rebate_pc;
    }

    get agTotal(): number {
        if (!this.settledStakePcSum || this.settledStakePcSum === 0) {
            return 0;
        }
        return this.balanceAgentPc + this.playerComPc;
    }

    get balanceAgent(): number {
        return this.bookmakerId === null ? this.balanceAgentPc : this.balanceAgentAc;
    }

    get bookBalanceDisplayed(): number {
        return this.bookmakerId === null ? this.balanceBookmakerEuro : this.balanceBookmakerAc;
    }

    get bookCommDisplayed(): number {
        return this.bookmakerId === null ? this.companyComEuro : this.companyComAc;
    }

    get bookFeesDisplayed(): number {
        return this.bookmakerId === null ? this.bookFeesEuro : this.bookFeesAc;
    }

    get bookStakedDisplayed(): number {
        return this.bookmakerId === null ? this.settledStakeBookmakerSumPc : this.settledStakeBookmakerAcSum;
    }

    get bookTotalAc(): number {
        return this.balanceBookmakerAc + this.companyComAc - this.bookFeesAc + this.bookRebateAc;
    }

    get bookTotalDisplayed(): number {
        return this.bookmakerId === null ? this.bookTotalEuro : this.bookTotalAc;
    }

    get bookTotalEuro(): number {
        return this.balanceBookmakerEuro + this.companyComEuro - this.bookFeesEuro + this.bookRebateEuro;
    }

    get bookTurnoverDisplayed(): number {
        return this.bookmakerId === null ? this.absBalanceBookmakerPc : this.absBalanceBookmaker;
    }

    get companyPLAc(): number {
        return this.balanceBookmakerAc - this.balanceAgentAc + this.companyComAc - this.playerComAc - this.bookFeesAc + this.bookRebateAc;
    }

    get companyPLDisplayed(): number {
        return this.bookmakerId === null ? this.companyPLEuro : this.companyPLAc;
    }

    get companyPLEuro(): number {
        return this.balanceBookmakerEuro - this.balanceAgentEuro + this.companyComEuro - this.playerComEuro - this.bookFeesEuro + this.bookRebateEuro;
    }

    get feesRebateDisplayed(): number {
        return this.bookmakerId === null ? this.bookRebateEuro : this.bookRebateAc;
    }

    get payCommsDisplayed(): number {
        return this.bookmakerId === null ? this.playerComEuro : this.playerComPc;
    }

    get playerPL(): number {
        return this.balancePc + this.playerComPc;
    }

    get playerYield(): number {
        if (this.settledStakePcSum === 0) {
            return 0;
        }
        return (this.balancePc / this.settledStakePcSum) * 100;
    }

    getAgBalanceCurrency(): number {
        return this.bookmakerId === null ? this.currencyId || Currency.Euro : this.accountCurrencyId!;
    }

    getAgTurnoverCurrency(): number {
        return this.currencyId || Currency.Euro;
    }

    getBookBalanceCurrency(): number {
        return this.bookmakerId === null ? Currency.Euro : this.accountCurrencyId!;
    }

    getBookCommCurrency(): number {
        return this.bookmakerId === null ? Currency.Euro : this.accountCurrencyId!;
    }

    getBookFeesCurrency(): number {
        return this.bookmakerId === null ? Currency.Euro : this.accountCurrencyId!;
    }

    getBookStakedCurrency(): number {
        return this.bookmakerId === null ? this.currencyId || Currency.Euro : this.accountCurrencyId!;
    }

    getBookTotalCurrency(): number {
        return this.bookmakerId === null ? Currency.Euro : this.accountCurrencyId!;
    }

    getBookTurnoverCurrency(): number {
        return this.bookmakerId === null ? this.currencyId || Currency.Euro : this.accountCurrencyId!;
    }

    getCompanyPLCurrency(): number {
        return this.bookmakerId === null ? Currency.Euro : this.accountCurrencyId!;
    }

    getCoPositionBalanceCurrency(): number {
        return this.currencyId || Currency.Euro;
    }

    getFeesRebateCurrency(): number {
        return this.bookmakerId === null ? Currency.Euro : this.accountCurrencyId!;
    }

    getPayCommsCurrency(): number {
        return this.bookmakerId === null || this.currencyId === null ? Currency.Euro : this.currencyId!;
    }

    getPlayerBalanceCurrency(): number {
        return this.currencyId || Currency.Euro;
    }

    getPositionCurrency(): number {
        return this.currencyId || Currency.Euro;
    }

    getStakedCurrency(): number {
        return this.currencyId || Currency.Euro;
    }

    getTurnoverCurrency(): number {
        return this.currencyId || Currency.Euro;
    }
}
